import React, { useState } from 'react';
import styled from 'styled-components';
import { SharedLayout } from '../components/layout/SharedLayout';
import { Link } from 'react-router-dom';
import { theme } from '../styles/theme';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const HeroSection = styled.section`
  background-color: white;
  padding: 60px 20px;
  text-align: center;
  border-bottom: 1px solid ${theme.colors.neutral.lightGrey};
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.sizes.xl};
  color: ${theme.colors.neutral.grey};
  margin-bottom: ${theme.spacing.lg};
`;

const OptionsSection = styled.section`
  max-width: 1000px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const OptionCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 40px;
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 30px 20px;
  }
`;

const OptionNumber = styled.div`
  background-color: #FFD700;
  color: #232B3B;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.fontWeights.bold};
  margin: 0 auto 24px;
`;

const OptionTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const OptionDescription = styled.p`
  color: ${theme.colors.neutral.grey};
  margin-bottom: 24px;
  line-height: 1.6;
`;

const PhoneLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background-color: #FFD700;
  color: #232B3B;
  font-weight: ${theme.typography.fontWeights.bold};
  padding: 14px 28px;
  border-radius: ${theme.borderRadius.md};
  text-decoration: none;
  font-size: ${theme.typography.sizes.lg};
  transition: background-color ${theme.transitions.fast};
  
  &:hover {
    background-color: #e6c200;
    text-decoration: none;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ExternalButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background-color: #FFD700;
  color: #232B3B;
  font-weight: ${theme.typography.fontWeights.bold};
  padding: 14px 28px;
  border-radius: ${theme.borderRadius.md};
  text-decoration: none;
  font-size: ${theme.typography.sizes.lg};
  transition: background-color ${theme.transitions.fast};
  
  &:hover {
    background-color: #e6c200;
    text-decoration: none;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const DifferenceSection = styled.section`
  background-color: white;
  padding: 60px 20px;
`;

const DifferenceContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  text-align: center;
  margin-bottom: 40px;
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const CheckIcon = styled.div`
  color: #FFD700;
  font-size: 24px;
  flex-shrink: 0;
`;

const BenefitText = styled.div``;

const BenefitTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.xs};
`;

const BenefitDescription = styled.p`
  color: ${theme.colors.neutral.grey};
  font-size: ${theme.typography.sizes.md};
`;

const TestimonialSection = styled.section`
  background-color: #f9f9f9;
  padding: 60px 20px;
`;

const TestimonialContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const Quote = styled.blockquote`
  font-size: ${theme.typography.sizes.xl};
  font-style: italic;
  color: ${theme.colors.text.primary};
  margin-bottom: 24px;
  line-height: 1.6;
`;

const Attribution = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const AuthorName = styled.p`
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.sizes.lg};
`;

const AuthorTitle = styled.p`
  color: ${theme.colors.neutral.grey};
  font-size: ${theme.typography.sizes.md};
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  position: relative;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
`;

const ModalText = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 25px;
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 20px;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
  }
`;

const ModalButton = styled.button`
  background: #ffd700;
  color: #333;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  margin-right: 10px;
  
  &:hover {
    background: #e6c200;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: 1px solid #ddd;
  color: #666;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const FindAdvisor: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState('');

  const handleRetirevanaClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const handleSubmitEmail = () => {
    if (email) {
      alert('Thank you! We\'ll notify you when Retirevana launches.');
      setIsModalOpen(false);
      setEmail('');
    }
  };

  return (
    <SharedLayout>
      <PageWrapper>
        <HeroSection>
          <HeroContent>
            <PageTitle>Work With Advisors Who Own What They Recommend</PageTitle>
            <PageSubtitle>
              Connect with pre-vetted advisors who use our AI tools for every recommendation
            </PageSubtitle>
          </HeroContent>
        </HeroSection>

        <OptionsSection>
          <OptionsGrid>
            <OptionCard>
              <OptionNumber>1</OptionNumber>
              <OptionTitle>Quick Phone Call</OptionTitle>
              <OptionDescription>
                Talk to our team first. We'll understand your needs and make the right match.
                No sales pressure, just honest conversation.
              </OptionDescription>
              <PhoneLink to="/contact">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                800-922-1894
              </PhoneLink>
            </OptionCard>

            <OptionCard>
              <OptionNumber>2</OptionNumber>
              <OptionTitle>Get Matched Online</OptionTitle>
              <OptionDescription>
                Answer a few questions and get matched with pre-vetted advisors in your area.
                Compare credentials and choose who you connect with.
              </OptionDescription>
              <ExternalButton href="#" onClick={handleRetirevanaClick}>
                Go to Retirevana
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </ExternalButton>
            </OptionCard>
          </OptionsGrid>
        </OptionsSection>

        <DifferenceSection>
          <DifferenceContent>
            <SectionTitle>What Makes Our Advisors Different</SectionTitle>
            <BenefitsList>
              <BenefitItem>
                <CheckIcon>✓</CheckIcon>
                <BenefitText>
                  <BenefitTitle>They Own What They Sell</BenefitTitle>
                  <BenefitDescription>
                    Every advisor personally owns the products they recommend
                  </BenefitDescription>
                </BenefitText>
              </BenefitItem>

              <BenefitItem>
                <CheckIcon>✓</CheckIcon>
                <BenefitText>
                  <BenefitTitle>AI-Powered Analysis</BenefitTitle>
                  <BenefitDescription>
                    Use our backtesting tools for every recommendation
                  </BenefitDescription>
                </BenefitText>
              </BenefitItem>

              <BenefitItem>
                <CheckIcon>✓</CheckIcon>
                <BenefitText>
                  <BenefitTitle>No Captive Agents</BenefitTitle>
                  <BenefitDescription>
                    They can sell ANY annuity, not just one company's products
                  </BenefitDescription>
                </BenefitText>
              </BenefitItem>

              <BenefitItem>
                <CheckIcon>✓</CheckIcon>
                <BenefitText>
                  <BenefitTitle>Verified Track Record</BenefitTitle>
                  <BenefitDescription>
                    Pre-screened advisors with documented happy clients
                  </BenefitDescription>
                </BenefitText>
              </BenefitItem>

              <BenefitItem>
                <CheckIcon>✓</CheckIcon>
                <BenefitText>
                  <BenefitTitle>Full Transparency</BenefitTitle>
                  <BenefitDescription>
                    They'll show you exactly how they get paid
                  </BenefitDescription>
                </BenefitText>
              </BenefitItem>

              <BenefitItem>
                <CheckIcon>✓</CheckIcon>
                <BenefitText>
                  <BenefitTitle>No Pressure</BenefitTitle>
                  <BenefitDescription>
                    Education first, sales second - always
                  </BenefitDescription>
                </BenefitText>
              </BenefitItem>
            </BenefitsList>
          </DifferenceContent>
        </DifferenceSection>

        <TestimonialSection>
          <TestimonialContent>
            <Quote>
              "I will never recommend or sell any product unless I own it myself. 
              That's not just a promise - it's how I've built my entire practice."
            </Quote>
            <Attribution>
              <AuthorName>Carleton Averill Sr.</AuthorName>
              <AuthorTitle>Founder, Annuity Answers</AuthorTitle>
            </Attribution>
          </TestimonialContent>
        </TestimonialSection>
        
        <Modal isOpen={isModalOpen}>
          <ModalContent>
            <ModalTitle>Coming Soon!</ModalTitle>
            <ModalText>Enter your email below to be notified when we launch.</ModalText>
            <EmailInput
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div>
              <ModalButton onClick={handleSubmitEmail}>Notify Me</ModalButton>
              <CloseButton onClick={() => setIsModalOpen(false)}>Close</CloseButton>
            </div>
          </ModalContent>
        </Modal>
      </PageWrapper>
    </SharedLayout>
  );
};

export default FindAdvisor;